// FILE: apps/admin/src/components/UserPicker.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Accessible searchable user combobox/dropdown for selecting players or operators by @username, display name, Telegram ID, or UUID with live RUB/GRAM balance preview.
//   SCOPE: PickedAdminUser, UserPickerProps, normalizeUserPickerQuery, and UserPicker component.
//   DEPENDS: ../api.js, ./Icons.js, ./UserAvatar.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   PickedAdminUser - User summary record returned by /admin/users for picker options and selected card
//   UserPicker - Searchable combobox dropdown loading up to 100 users immediately and supporting direct @username or Telegram ID input
//   UserPickerProps - Props for UserPicker component
//   normalizeUserPickerQuery - Strip leading @ or # prefixes and trim search input before querying /admin/users
// END_MODULE_MAP

import { useQuery } from '@tanstack/react-query';
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';

import { api, gram, rub } from '../api.js';
import { AdminIcon } from './Icons.js';
import { resolveAdminUserDisplayName, UserAvatar } from './UserAvatar.js';

export interface PickedAdminUser {
  id: string;
  telegramId: number;
  username: string | null;
  firstName: string | null;
  customName?: string | null;
  photoUrl?: string | null;
  customPhotoUrl?: string | null;
  balanceKopecks: number;
  balanceMilliGram?: number;
}

export interface UserPickerProps {
  value: string;
  onChange: (identifier: string, user?: PickedAdminUser | null) => void;
  placeholder?: string;
  label?: string;
}

// START_CONTRACT: normalizeUserPickerQuery
//   PURPOSE: Normalize operator search text by trimming whitespace and stripping leading @ or # prefixes for /admin/users?q=.
//   INPUTS: { raw: string }
//   OUTPUTS: { string }
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: normalizeUserPickerQuery
export function normalizeUserPickerQuery(raw: string): string {
  // START_BLOCK_NORMALIZE_USER_PICKER_QUERY
  return raw.trim().replace(/^[@#]+/, '').trim();
  // END_BLOCK_NORMALIZE_USER_PICKER_QUERY
}

function formatUserInputLabel(u: PickedAdminUser): string {
  const display = resolveAdminUserDisplayName(u);
  if (u.username) return `@${u.username}`;
  return `${display.primary} (#${u.telegramId})`;
}

// START_CONTRACT: UserPicker
//   PURPOSE: Render searchable user combobox dropdown with immediate initial list, @username/TG ID/UUID recognition, and selected-user balance summary card.
//   INPUTS: UserPickerProps
//   OUTPUTS: React element
//   SIDE_EFFECTS: Queries GET /admin/users?q= via TanStack Query and invokes onChange with selected UUID or typed identifier
//   LINKS: M-ADMIN
// END_CONTRACT: UserPicker
export function UserPicker({
  value,
  onChange,
  placeholder = 'Выберите из списка или введите @username, имя, TG ID, UUID…',
  label,
}: UserPickerProps) {
  // START_BLOCK_USER_PICKER
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const lastEmittedRef = useRef<string>(value);

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState<string>(value);
  const [isFiltering, setIsFiltering] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [pickedUser, setPickedUser] = useState<PickedAdminUser | null>(null);
  const [highlightIdx, setHighlightIdx] = useState(0);

  useEffect(() => {
    const rawQuery = isFiltering ? normalizeUserPickerQuery(searchText) : '';
    const timer = window.setTimeout(() => {
      setDebouncedQuery(rawQuery);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [searchText, isFiltering]);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    if (!value.trim()) {
      setPickedUser(null);
      setSearchText('');
      setIsFiltering(false);
      return;
    }
    setSearchText(value);
    setIsFiltering(false);
  }, [value]);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setIsFiltering(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const { data: rawUsers, isFetching } = useQuery({
    queryKey: ['user-picker', debouncedQuery],
    queryFn: async () => {
      const res = await api.get<PickedAdminUser[] | { items?: PickedAdminUser[] }>(
        `/admin/users?q=${encodeURIComponent(debouncedQuery)}`,
      );
      return Array.isArray(res) ? res : (res?.items ?? []);
    },
    staleTime: 15_000,
  });

  const users = useMemo(() => rawUsers ?? [], [rawUsers]);

  const activeUser = useMemo<PickedAdminUser | null>(() => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const cleanLower = normalizeUserPickerQuery(trimmed).toLowerCase();

    if (
      pickedUser &&
      (pickedUser.id === trimmed ||
        String(pickedUser.telegramId) === cleanLower ||
        (pickedUser.username && pickedUser.username.toLowerCase() === cleanLower))
    ) {
      return pickedUser;
    }

    return (
      users.find(
        (u) =>
          u.id === trimmed ||
          String(u.telegramId) === cleanLower ||
          Boolean(u.username && u.username.toLowerCase() === cleanLower),
      ) ?? null
    );
  }, [value, pickedUser, users]);

  const handleSelectUser = (u: PickedAdminUser) => {
    lastEmittedRef.current = u.id;
    setPickedUser(u);
    setSearchText(formatUserInputLabel(u));
    setIsFiltering(false);
    setOpen(false);
    onChange(u.id, u);
  };

  const handleReset = () => {
    lastEmittedRef.current = '';
    setPickedUser(null);
    setSearchText('');
    setIsFiltering(false);
    setDebouncedQuery('');
    onChange('', null);
  };

  const handleInputChange = (next: string) => {
    setSearchText(next);
    setIsFiltering(true);
    setOpen(true);
    setHighlightIdx(0);

    const trimmed = next.trim();
    const cleanLower = normalizeUserPickerQuery(trimmed).toLowerCase();
    const exactMatch = trimmed
      ? (users.find(
          (u) =>
            u.id === trimmed ||
            String(u.telegramId) === cleanLower ||
            Boolean(u.username && u.username.toLowerCase() === cleanLower),
        ) ?? null)
      : null;

    setPickedUser(exactMatch);
    lastEmittedRef.current = trimmed;
    onChange(trimmed, exactMatch);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setIsFiltering(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (users.length > 0) {
        setHighlightIdx((idx) => (idx + 1) % users.length);
      }
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (open && users.length > 0) {
        setHighlightIdx((idx) => (idx - 1 + users.length) % users.length);
      }
      return;
    }
    if (e.key === 'Enter' && open && users.length > 0) {
      const candidate = users[highlightIdx];
      if (candidate) {
        e.preventDefault();
        handleSelectUser(candidate);
      }
    }
  };

  const activeDisplay = activeUser ? resolveAdminUserDisplayName(activeUser) : null;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {label && <label className="field-label">{label}</label>}

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <span
          style={{
            position: 'absolute',
            left: 11,
            color: 'var(--ink-faint)',
            pointerEvents: 'none',
            display: 'inline-flex',
          }}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>

        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          style={{ width: '100%', paddingLeft: 34, paddingRight: searchText || value ? 68 : 36 }}
          placeholder={placeholder}
          value={searchText}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {(searchText || value) && (
          <button
            type="button"
            title="Очистить выбор"
            aria-label="Очистить выбор"
            onClick={handleReset}
            style={{
              position: 'absolute',
              right: 34,
              width: 30,
              height: 30,
              background: 'none',
              border: 'none',
              color: 'var(--ink-faint)',
              cursor: 'pointer',
              padding: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AdminIcon name="close" size={13} />
          </button>
        )}

        <button
          type="button"
          tabIndex={-1}
          aria-label="Открыть список пользователей"
          onClick={() => setOpen((prev) => !prev)}
          style={{
            position: 'absolute',
            right: 4,
            width: 30,
            height: 30,
            background: 'none',
            border: 'none',
            color: 'var(--ink-faint)',
            cursor: 'pointer',
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease',
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 60,
            maxHeight: 'min(300px, 48dvh)',
            overflowY: 'auto',
            background: 'var(--bg-1)',
            border: '1px solid var(--hair-2)',
            borderRadius: 12,
            padding: 6,
            boxShadow: '0 20px 44px rgba(8, 12, 22, 0.72)',
          }}
        >
          <div
            className="muted"
            style={{
              fontSize: 11,
              padding: '4px 8px 6px',
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--hair)',
              marginBottom: 4,
            }}
          >
            <span>
              {isFetching
                ? 'Поиск пользователей…'
                : debouncedQuery
                  ? `Найдено по «${debouncedQuery}»: ${users.length}`
                  : `Список пользователей (${users.length})`}
            </span>
            <span>Кликните для выбора</span>
          </div>

          {users.map((u, idx) => {
            const disp = resolveAdminUserDisplayName(u);
            const isSelected = activeUser?.id === u.id;
            const isHighlighted = idx === highlightIdx;
            return (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className="user-picker-option"
                onMouseEnter={() => setHighlightIdx(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectUser(u);
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: isSelected
                    ? 'rgba(130, 148, 255, 0.16)'
                    : isHighlighted
                      ? 'var(--bg-2)'
                      : 'transparent',
                  border: 'none',
                  borderRadius: 9,
                  padding: '8px 10px',
                  color: 'var(--ink)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                }}
              >
                <div className="user-cell" style={{ minWidth: 0 }}>
                  <UserAvatar
                    photoUrl={u.photoUrl}
                    customPhotoUrl={u.customPhotoUrl}
                    name={disp.primary}
                    size={32}
                  />
                  <div className="user-cell-meta">
                    <div className="user-cell-name-row">
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{disp.primary}</span>
                      {disp.originalTgName && (
                        <span className="tg-orig-tag">TG: {disp.originalTgName}</span>
                      )}
                    </div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      <span>{u.username ? `@${u.username}` : 'без username'}</span>
                      {' · '}
                      <span className="mono">TG #{u.telegramId}</span>
                    </div>
                  </div>
                </div>

                <div
                  className="mono"
                  style={{
                    fontSize: 12,
                    color: 'var(--ink-soft)',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {rub(u.balanceKopecks)} ₽ · {gram(u.balanceMilliGram ?? 0)} GRAM
                </div>
              </button>
            );
          })}

          {users.length === 0 && !isFetching && (
            <div className="muted" style={{ padding: '14px 10px', fontSize: 12.5, textAlign: 'center' }}>
              <div>В базе Mini App совпадений не найдено.</div>
              {value.trim() && (
                <div style={{ marginTop: 4 }}>
                  Будет передан введённый идентификатор:{' '}
                  <strong className="mono" style={{ color: 'var(--ink)' }}>
                    {value.trim()}
                  </strong>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeUser && activeDisplay && (
        <div
          style={{
            marginTop: 8,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'var(--bg-2)',
            border: '1px solid rgba(130, 148, 255, 0.28)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <div className="user-cell">
            <UserAvatar
              photoUrl={activeUser.photoUrl}
              customPhotoUrl={activeUser.customPhotoUrl}
              name={activeDisplay.primary}
              size={34}
            />
            <div className="user-cell-meta">
              <div className="user-cell-name-row">
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{activeDisplay.primary}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                  {activeUser.username ? `@${activeUser.username}` : 'без username'}
                </span>
                <span className="chip info mono" style={{ padding: '2px 7px', fontSize: 11 }}>
                  TG #{activeUser.telegramId}
                </span>
              </div>
              <div className="mono" style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                Баланс: <strong>{rub(activeUser.balanceKopecks)} ₽</strong> ·{' '}
                <strong>{gram(activeUser.balanceMilliGram ?? 0)} GRAM</strong>
              </div>
            </div>
          </div>

          <button type="button" className="btn ghost sm" onClick={handleReset}>
            <AdminIcon name="close" size={12} />
            <span>Сбросить</span>
          </button>
        </div>
      )}
    </div>
  );
  // END_BLOCK_USER_PICKER
}
